import React from 'react'

import type { DragEndEvent } from '@dnd-kit/core'
import { DndContext } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext } from '@dnd-kit/sortable'

import { DragIndicatorIcon } from '../svgIcons'
import { SortableList } from './SortableList'
import { SortableListItem } from './SortableListItem'

import { Count, Description, DragHandle, TabContainer, Title } from './styles'

interface SortableItem {
  description: StringOrMessage
  id: string
  title: StringOrMessage
}

interface VerticalSortableListProps<Item extends SortableItem> {
  items: Item[]
  onSave: (orderedItems: Item[]) => void
}

export function VerticalSortableList<Item extends SortableItem>({ items, onSave }: VerticalSortableListProps<Item>) {
  const onDragEndHandler = React.useCallback(
    ({ active, over }: DragEndEvent) => {
      if (over && active.id !== over.id) {
        const activeIndex = items.findIndex(item => item.id === active.id)
        const overIndex = items.findIndex(item => item.id === over.id)

        onSave(arrayMove(items, activeIndex, overIndex))
      }
    },
    [items, onSave]
  )

  return (
    <DndContext onDragEnd={onDragEndHandler} modifiers={[restrictToVerticalAxis]}>
      <SortableContext items={items}>
        <SortableList>
          {items.map((row, idx) => (
            <SortableListItem key={row.id} id={row.id}>
              <TabContainer>
                <DragHandle data-testid="drag-handle">
                  <DragIndicatorIcon size={20} />
                </DragHandle>
                <Count>{idx + 1}.</Count>
                <Title>{row.title}</Title>
                <Description>{row.description}</Description>
              </TabContainer>
            </SortableListItem>
          ))}
        </SortableList>
      </SortableContext>
    </DndContext>
  )
}
